import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, XCircle, X } from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';
import { money } from '../../../utils/nurseryMoney';
import { cn } from '../../../utils/cn';
import { PoLineMatcher, type MatchOption } from './PoLineMatcher';
import type { PoImportFile, PoImportLine, PoImportResult, PoLineIssue } from '../../../types/nurseryRetail';

/**
 * One purchase order on the import page: what the file says, what we made of
 * it, and anything holding it back. A held purchase order says why in red and
 * is not imported until that is sorted; amber is a warning and holds nothing.
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';

const ISSUE_LABELS: Record<PoLineIssue, string> = {
  unmatched: 'Not matched',
  inactive: 'No longer sold',
  no_price: 'No price',
  price_mismatch: 'Price differs',
  over_stock: 'More than is free',
  pot_size: 'Pot size differs',
  no_quantity: 'No quantity',
  barcode_damaged: 'Barcode damaged',
};

// Each of these holds the purchase order back; the rest only warn.
const HOLDING: PoLineIssue[] = ['unmatched', 'inactive', 'no_price', 'price_mismatch'];

const VIA_LABELS = { barcode: 'barcode', remembered_barcode: 'matched before', item_number: 'item number' } as const;
const KIND_LABELS = { stock: null, trade: 'Trade order', customer: 'Customer order' } as const;

function Line({ line, options, busy, onMatch }: {
  line: PoImportLine;
  options: MatchOption[];
  busy: boolean;
  onMatch: (line: PoImportLine, option: MatchOption) => void;
}) {
  const skipped = line.issues.includes('no_quantity');
  return (
    <li className={cn('px-3 py-2 text-sm', skipped && 'opacity-60')}>
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 break-words text-neutral-900">{line.description || 'No description'}</span>
        <span className="flex-shrink-0 font-semibold text-neutral-900">× {line.quantity}</span>
      </div>
      {line.match ? (
        <p className="text-neutral-600 break-words">
          {line.match.name}{line.match.tree_type_name ? ` · ${line.match.tree_type_name}` : ''}
          <span className="text-neutral-400"> ({VIA_LABELS[line.match.via]})</span>
        </p>
      ) : !skipped && (
        <p className="text-xs text-neutral-500 break-words">
          {[line.barcode ? `Barcode ${line.barcode}` : null, line.item_number ? `their item ${line.item_number}` : null]
            .filter(Boolean).join(' · ') || 'No barcode or item number on the line'}
        </p>
      )}
      {!skipped && (
        <p className="text-xs text-neutral-600 break-words">
          Purchase order {line.po_unit_price == null ? 'has no price' : money(line.po_unit_price)}
          {line.match && (
            <>
              {' · '}ours {line.our_unit_price == null ? 'not priced' : money(line.our_unit_price)}
              {line.price_source ? ` (${line.price_source})` : ''}
              {line.free != null ? ` · ${line.free} free` : ''}
            </>
          )}
        </p>
      )}
      {line.comment && <p className="text-xs text-neutral-500 break-words">Comment: {line.comment}</p>}
      {line.issues.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {line.issues.map((issue) => (
            <span key={issue} className={cn('rounded-full text-xs font-medium px-2 py-0.5',
              HOLDING.includes(issue) ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800')}>
              {ISSUE_LABELS[issue]}
            </span>
          ))}
        </div>
      )}
      {line.issues.includes('unmatched') && (line.barcode || line.item_number) && (
        <PoLineMatcher line={line} options={options} busy={busy} onMatch={(o) => onMatch(line, o)} />
      )}
    </li>
  );
}

export function PoImportCard({ result, file, farms, options, busy, onChange, onRemove, onMatch }: {
  result: PoImportResult;
  /** The file as it will be sent again; absent once the purchase order is imported. */
  file?: PoImportFile;
  farms: { id: number; name: string }[];
  options: MatchOption[];
  busy: boolean;
  onChange?: (patch: Partial<PoImportFile>) => void;
  onRemove?: () => void;
  onMatch?: (line: PoImportLine, option: MatchOption) => void;
}) {
  const tz = useOrgTimezone();
  const { po, pick } = result;
  const created = result.created === true;
  const key = result.file_hash;
  const date = (d: string | null | undefined) => (d ? formatLocalDateInTz(d, tz) : 'no date');

  const madeUp = () => {
    if (!result.despatch_date) return 'No day';
    const parts = [`${pick && pick.ok ? `${pick.day_name} ` : ''}${date(result.despatch_date)}`];
    if (pick && pick.ok && 'route' in pick) {
      if (pick.route) parts.push(`route ${pick.route}`);
      if (pick.del_seq) parts.push(`delivery ${pick.del_seq}`);
    }
    if (pick && pick.ok && pick.overridden) parts.push('chosen by hand');
    return parts.join(' · ');
  };

  const loadText = () => {
    if (!result.load) return 'Not put on a load';
    if (!result.load.joins) return `Not put on ${result.load.load_number}`;
    if (result.load.load_number) return `${created ? 'On' : 'Joins'} ${result.load.load_number}`;
    return 'A new cross dock load for that day';
  };

  return (
    <li className={cn('bg-white border rounded-xl overflow-hidden',
      created ? 'border-green-300' : result.ready ? 'border-neutral-200' : 'border-red-300')}>
      <div className="px-4 py-3 flex items-start gap-3">
        {created
          ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
          : result.ready
            ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-neutral-400 mt-0.5" />
            : <XCircle className="h-5 w-5 flex-shrink-0 text-red-600 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-semibold text-neutral-900 break-all">
              {po ? `PO ${po.po_number}` : (result.file_name || 'Unreadable file')}
            </h3>
            <span className={cn('rounded-full text-xs font-medium px-2 py-0.5',
              created ? 'bg-green-100 text-green-800' : result.ready ? 'bg-neutral-100 text-neutral-700' : 'bg-red-100 text-red-800')}>
              {created ? 'Imported' : result.ready ? 'Ready' : 'Held'}
            </span>
            {po && KIND_LABELS[po.po_kind] && (
              <span className="rounded-full bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5">{KIND_LABELS[po.po_kind]}</span>
            )}
          </div>
          {po && (
            <p className="text-sm text-neutral-600 break-words">
              {result.customer
                ? `${result.customer.name}${result.customer.parent_name ? ` (${result.customer.parent_name})` : ''} · store ${po.store_code}`
                : `Store ${po.store_code ?? 'not given'}${po.store_name ? ` · ${po.store_name}` : ''}`}
            </p>
          )}
          {created && result.order && (
            <p className="text-sm break-words">
              <Link to={`/nursery/orders/${result.order.id}`} className="text-primary-700 hover:underline">
                Order {result.order.order_number}
              </Link>
              {result.load?.id && result.load.joins && (
                <>
                  {' · '}
                  <Link to={`/nursery/loads/${result.load.id}`} className="text-primary-700 hover:underline">{result.load.load_number}</Link>
                </>
              )}
            </p>
          )}
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} disabled={busy} aria-label={`Remove ${po?.po_number ?? result.file_name ?? 'file'}`}
            className="flex-shrink-0 rounded p-1 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {result.problems.length > 0 && (
        <ul className="mx-4 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800 space-y-1">
          {result.problems.map((p) => (
            <li key={p.code} className="break-words">
              {p.message}
              {p.code === 'already_entered' && result.existing_order && (
                <> <Link to={`/nursery/orders/${result.existing_order.id}`} className="underline">Open the order</Link></>
              )}
            </li>
          ))}
        </ul>
      )}

      {result.warnings.length > 0 && (
        <ul className="mx-4 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 space-y-1">
          {result.warnings.map((w) => (
            <li key={w.code} className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 mt-0.5" />
              <span className="min-w-0 break-words">{w.message}</span>
            </li>
          ))}
        </ul>
      )}

      {!created && onChange && (result.customer_candidates?.length ?? 0) > 1 && (
        <div className="mx-4 mb-3">
          <label htmlFor={`${key}-customer`} className="block text-sm font-medium text-neutral-700 mb-1">Which customer is this order for?</label>
          <select id={`${key}-customer`} className={inputCls} value={file?.customer_id ?? ''} disabled={busy}
            onChange={(e) => onChange({ customer_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Choose…</option>
            {result.customer_candidates!.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.parent_name ? ` (${c.parent_name})` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {po && (
        <dl className="mx-4 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="text-neutral-500 flex-shrink-0">Asks for</dt>
            <dd className="min-w-0 break-words text-neutral-900">{date(po.deliver_on)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-neutral-500 flex-shrink-0">Made up</dt>
            <dd className="min-w-0 break-words text-neutral-900">{madeUp()}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-neutral-500 flex-shrink-0">From</dt>
            <dd className="min-w-0 break-words text-neutral-900">{result.despatch_farm?.name ?? 'No despatch farm'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-neutral-500 flex-shrink-0">Load</dt>
            <dd className="min-w-0 break-words text-neutral-900">{loadText()}</dd>
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <dt className="text-neutral-500 flex-shrink-0">Value</dt>
            <dd className="min-w-0 break-words text-neutral-900">
              {po.total_value == null ? 'not stated' : money(po.total_value)} on the purchase order
              {result.our_total != null && result.customer ? ` · ${money(result.our_total)} at our prices` : ''}
            </dd>
          </div>
        </dl>
      )}

      {!created && onChange && po && result.customer && (
        <details className="mx-4 mb-3 text-sm">
          <summary className="cursor-pointer text-primary-700">Change the farm or the day</summary>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor={`${key}-farm`} className="block text-sm font-medium text-neutral-700 mb-1">Despatch from</label>
              <select id={`${key}-farm`} className={inputCls} disabled={busy}
                value={file?.despatch_farm_id ?? ''}
                onChange={(e) => onChange({ despatch_farm_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">The customer's own farm</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor={`${key}-day`} className="block text-sm font-medium text-neutral-700 mb-1">Make it up on</label>
              <input id={`${key}-day`} type="date" className={inputCls} disabled={busy}
                value={file?.pick_date ?? ''}
                onChange={(e) => onChange({ pick_date: e.target.value || null })} />
              <p className="text-xs text-neutral-500 mt-1">
                Blank uses the cross dock schedule. A day chosen here is kept even if the schedule changes.
              </p>
            </div>
          </div>
        </details>
      )}

      {result.lines && result.lines.length > 0 && (
        <ul className="border-t border-neutral-100 divide-y divide-neutral-100">
          {result.lines.map((line, i) => (
            <Line key={`${line.line_number}-${i}`} line={line} options={options} busy={busy}
              onMatch={(l, o) => onMatch?.(l, o)} />
          ))}
        </ul>
      )}
    </li>
  );
}
